import SimpleCard from "lib/components/common/simple-card";
import TabBar from "lib/components/common/tab-bar";
import SimpleDataTable from "lib/components/common/simple-data-table";
import SimpleModal from "lib/components/common/simple-modal";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import SimpleTextbox from "lib/components/common/simple-textbox";
import SimpleButton from "lib/components/common/simple-button";
import DashboardLayout from "lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { useContext, useEffect, useState, useMemo } from "react";
import { AppContext } from "lib/contexts/app-context";
import { useNavigate, useLocation } from "react-router-dom";
import usePatients from "lib/hooks/usePatients";
import { Formik } from "formik";
import { Row, Col, Button, Form } from "react-bootstrap";
import FlexBox from "lib/components/common/flex-box";
import usePatientBookings from "lib/hooks/usePatientBookings";
import useClinics from "lib/hooks/useClinics";
import useMembershipPackages from "lib/hooks/useMembershipPackages";
import ErrorBox from "lib/components/common/error-box";
import usePackages from "lib/hooks/usePackages";
import useTherapies from "lib/hooks/useTherapies";
import useApi from "lib/hooks/useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { 
  PAITENT_SESSION_BOOKING_DETAILS_COLUMNS,
  PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS, 
  PATIENT_CREDITS_LIST_COLUMNS
} from "lib/constants/table-columns";
import { 
  DEFAULT_PATIENT_SESSION_BOOKING_DETAILS,
  DEFAULT_PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS
} from "lib/constants/default-values";
import PatientSessionBookingSummaryTile from "lib/components/common/patient/patient-session-booking-summary-tile";
import { SessionBookingContext, useSessionBooking } from "lib/contexts/session-booking-context";
import PatientMembershipPackageSummaryTile from "lib/components/common/patient/patient-membership-package-summary-tile";
import { toast } from "sonner";
import {
  PiHouseLight,
  PiShoppingCartLight,
  PiPackageLight,
  PiChatCenteredLight,
  PiCurrencyDollarSimpleLight,
  PiUserLight,
} from "react-icons/pi";
import SimpleEditor from "lib/components/common/editor/simple-editor";
import {
  EMAIL_VALIDATION_SCHEMA,
  NAME_VALIDATION_SCHEMA,
  PHONE_NUMBER_VALIDATION_SCHEMA,
  SIGN_UP_VALIDATION_SCHEME,
  POSITIVE_NUMBER_VALIDATION_SCHEMA
} from "lib/constants/validation-constants";
import { isAdminUser } from "lib/utils/util";

const breadcrumbItems = [
  {
    label: "Dashboard",
    path: PAGE_ROUTES.ADMIN.Dashboard,
  },
  {
    label: "Patients Management",
    path: PAGE_ROUTES.ADMIN.PatientsManagement,
  },
  {
    label: "Patient Profile",
    path: ''
  },
] as BreadcrumbItem[];

const PATIENT_PROFILE_TABS: GeneralItem[] = [
  {
    id: 1,
    label: "Consent Forms",
    icon: <PiHouseLight />,
  },
  {
    id: 2,
    label: "Packages",
    icon: <PiPackageLight />,
  },
  {
    id: 3,
    label: "Membership Packages",
    icon: <PiPackageLight />,
  },
  {
    id: 4,
    label: "Session Bookings",
    icon: <PiShoppingCartLight />,
  },
  {
    id: 5,
    label: "Notes",
    icon: <PiChatCenteredLight />,
  },
  {
    id: 6,
    label: "Credits List",
    icon: <PiCurrencyDollarSimpleLight />,
  },
  {
    id: 7,
    label: "Profile",
    icon: <PiUserLight />,
  },
];

const AdminPatientProfilePage = () => {
    const navigateTo = useNavigate();
    const { appState } = useContext(AppContext);

    // Only admin can show patient profile tab
    // Build tabs based on role (NO mutation)
    const PATIENT_PROFILE_TABS_LOCAL = useMemo(() => {
      return isAdminUser(appState.userRole)
        ? PATIENT_PROFILE_TABS
        : PATIENT_PROFILE_TABS.filter(tab => tab.id !== 7);
    }, [appState.userRole]);

    const location = useLocation();
    const patientId = location.state?.patientId;
    const patientName = location.state?.patientName;
    const [selectedTab, setSelectedTab] = useState<number>(1);
    
    const [sessionTherapies, setSessionTherapies] = useState<Therapy[]>([]);
    const [packages, setPackages] = useState<Package[]>([]);
    const [selectedClinicId, setSelectedClinicId] = useState<string>("");
    const [totalCredits, setTotalCredits] = useState<number>(0);
    const [patientCreditsList, setPatientCreditsList] = useState<PatientCreditsListResponse[]>([]);
    const {
        error,
        loading,
    } = usePatientBookings();
    const { getSessionEnabledTherapies } = useTherapies();
    const { getPackages, getPatientCredits, getPatientCreditsList } = usePackages();
    const { getSessionBookingsDetails, changeSessionBookingStatus, fetchConsentForm, revokeConsentForm, getPatientDetails, patientProfileForm, updateEnableDisablePatient, deletePatient, addCredits, loading: patientsLoading } = usePatients();
    const { getMembershipPackages, getBuyMembershipPackageList, changePatientMembershipPackageStatus } = useMembershipPackages();
    const { postData } = useApi<CheckoutSessionResponse>();
    const [sessionBookingsDetails, setSessionBookingsDetails] = useState<
    SessionBookingDetails[]
    >([]);
    const [showSessionBookingDetails, setShowSessionBookingDetails] = useState<boolean>(false);
    const [selectedSessionBookingDetails, setSelectedSessionBookingDetails] =
        useState<PatientSessionBookingDetails>(
        DEFAULT_PATIENT_SESSION_BOOKING_DETAILS
    );
    const [membershipPackages, setMembershipPackages] = useState<MembershipPackage[]>([]);
    const [buyMembershipPackagesList, setBuyMembershipPackagesList] = useState<BuyMembershipPackageList[]>([]);

    // Get session booking context safely
    const { updateSessionBookingState, sessionBookingState } = useSessionBooking();
    
    const [showMembershipPackageDetails, setShowMembershipPackageDetails] = useState<boolean>(false);
    const [selectedMembershipPackageDetails, setSelectedMembershipPackageDetails] =
    useState<BuyMembershipPackageList>(
      DEFAULT_PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS
    );
    const [consentForms, setConsentForms] = useState<any[]>([]);
    const [content, setContent] = useState<string>();
    const [patientDetail, setPatientDetails] = useState<PatientDetails>();
    const { clinics, getClinics } = useClinics();
    const [showAddPatientCredits, setShowAddPatientCredits] = useState<boolean>(false);

    const [creditValues, setCreditValues] = useState({
      credits: "",
      validity: "",
    });

  useEffect(() => {
    if (!patientId) return;
    // Fetch session-enabled therapies
    getSessionEnabledTherapies((list) => setSessionTherapies(list));
    // Fetch packages
    getPackages((list) => setPackages(list));
    // Fetch patient credits
    getPatientCredits(patientId, (res: any) => {
      setTotalCredits(Number(res.totalCreditsRemaining));
    });
    // Fetch patient credits list
    getPatientCreditsList(patientId, (res: any) => {
      setPatientCreditsList(res);
    });
    // fetch patient session bookings details by id
    getSessionBookingsDetails(patientId, (bookingsessions) => {
        setSessionBookingsDetails(bookingsessions);
    });
    getMembershipPackages((list) => setMembershipPackages(list));
    getBuyMembershipPackageList(patientId, (list) => {
      setBuyMembershipPackagesList(list);
    });

    fetchConsentForm(patientId, (response) => {
      setConsentForms(Array.isArray(response) ? response : []); 
    });

    getPatientDetails(patientId, (res: any) => {
      setPatientDetails(res);
    });
    getClinics(true);
  }, [patientId]);

  const handleBuyPackage = (packageId: string) => {
    postData(
      API_ROUTES.PACKAGES.buy.replace(":packageId", packageId),
      {
        patientId: patientId,
      },
      (response) => {
        console.log("package buy => response", response);
        window.location.href = response.url;
      } 
    );
  };

  // Handler to update session booking status using API
  const onChangeSessionBookingStatus = async (sessionId: string, newStatus: string) => {
    try {
      await changeSessionBookingStatus(patientId, sessionId, newStatus);
      setSessionBookingsDetails((prev) =>
        prev.map((session) =>
          session.id === sessionId ? { ...session, status: newStatus } : session
        )
      );
      toast.success(`Session status updated to '${newStatus}'.`);
    } catch (error) {
      toast.error('Failed to update session status. Please try again.');
    }
  };

  const handleBookSession = (therapyId: string) => {

    if (!selectedClinicId || selectedClinicId === "all") {
      alert("Please select a clinic before booking the session.");
      return;
    }

    // Merge therapyId into existing booking state
    updateSessionBookingState({
      ...sessionBookingState,
      clinicId: selectedClinicId,
      therapyId: therapyId,
      patientId: patientId,
      patientName: patientName,
    });
    navigateTo(PAGE_ROUTES.PATIENT.ConfirmTherapySession);
  };

  const handleClinicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClinicId(e.target.value);
  };

  const handleBuyMembershipPackage = (membershipPackageId: string) => {
    postData(
      API_ROUTES.MEMBERSHIPPACKAGES.buy.replace(":membershipPackageId", membershipPackageId),
      {
        patientId: patientId,
      },
      (response) => {
        console.log("package buy => response", response);
        window.location.href = response.url;
      }
    );
  };

  // Handler to update session booking status using API
  const onChangePatientMembershipPackageStatus = async (patientMembershipPackageId: string, status: string) => {
    try {
      await changePatientMembershipPackageStatus(patientId, patientMembershipPackageId, status);
      setSelectedMembershipPackageDetails((prev) =>
        prev.id === patientMembershipPackageId ? { ...prev, status: status } : prev
      );
      getBuyMembershipPackageList(patientId, (list) => {
        setBuyMembershipPackagesList(list);
      });
      toast.success(`Session status updated to '${status}'.`);
    } catch (error) {
      toast.error('Failed to update session status. Please try again.');
    }
  };

  const handleAirPod = () => {
    navigateTo(PAGE_ROUTES.ADMIN.AirpodForm, {state: {patientId: patientId}});
  }

  const handleCryoChamber = () => {
    navigateTo(PAGE_ROUTES.ADMIN.CryoChamberForm, {state: {patientId: patientId}});
  }

  const handleInfrared = () => {
    navigateTo(PAGE_ROUTES.ADMIN.infraredConsentForm, {state: {patientId: patientId}});
  }

  const getFormByType = (type: string) => {
    return consentForms.find((item) => item.parentType === type);
  };

  const handleView = (url: string) => {
    window.open(url, "_blank");
  };

  const handleRevoke = (id: string) => {
    if (window.confirm("Are you sure you want to revoke this consent form?")) {
      revokeConsentForm(patientId, id, () => {
        fetchConsentForm(patientId, (response) => {
          setConsentForms(Array.isArray(response) ? response : []); 
        });
      });
    }
    console.log("Revoke form =>", id);
    // Call API here to revoke -> then refresh fetchConsentForm()
  };

  const onEnableDisablePatient = (patientId: string, disabled: boolean) => {
    const message = disabled
    ? "Are you sure you want to disable this patient?"
    : "Are you sure you want to enable this patient?";

    if (!window.confirm(message)) {
      return;
    }
    
    updateEnableDisablePatient(patientId, disabled, (response) => {
      setPatientDetails(response[0]);
      toast.success("Status updated successfully");
    });
  };

  const onDeletePatient = (patientId: string) => {
    if (!window.confirm("Are you sure you want to delete this patient?")) {
      return; // stop delete
    }

    deletePatient(patientId, () => {
      navigateTo(PAGE_ROUTES.ADMIN.PatientsManagement);
      toast.success("Patient deleted successfully");
    });
  };

  const handleAddCredits = () => {
    setCreditValues({
      credits: "",
      validity: "",
    });
    setShowAddPatientCredits(true);
    // navigateTo(PAGE_ROUTES.ADMIN.AddCredits, {state: {patientId: patientId}});
  };

  const handleCreditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreditValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addCredits(patientId, creditValues.credits, creditValues.validity, () => {
      // navigateTo(PAGE_ROUTES.ADMIN.PatientsManagement);
      toast.success("Credits added to patient account successfully");
    });
    // TODO: call API to add credits here
    setShowAddPatientCredits(false);
    window.location.reload();
  };

  // Show loading state while initializing
  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading booking options...</p>
      </div>
    );
  }

  if (error) return <ErrorBox message={error} />;

  return (
    <DashboardLayout breadcrumbItems={breadcrumbItems}>

    <SimpleCard>
        <FlexBox>
          <h4>{`${patientName}'s credit balance: ${totalCredits}`}</h4>
          <TabBar
            tabs={PATIENT_PROFILE_TABS}
            selectedTabId={selectedTab}
            onTabChange={setSelectedTab}
          />
        </FlexBox>
        <hr/>
        {selectedTab === 1 && (
          <Row>
            <Col xs={12} lg={12}>
              <SimpleCard
                title="Consent Forms"
                className="shadow-sm"
              >
                <FlexBox
                  direction="row"
                  gap="3"
                  justifyContent="left"
                  className="flex-wrap justify-content-center justify-content-lg-start"
                >
                  
                    <SimpleCard className="membership-package-card mb-0 mb-md-3">
                      <FlexBox
                        direction="column"
                        alignItems="center"
                        justifyContent="between"
                        className="h-100 p-0 p-md-4"
                      >
                        <div className="w-100 text-center">
                          <h5 className="fw-bold text-primary">Infrared Souna</h5>
                        </div>
                        <FlexBox gap="2">
                          {getFormByType("infrared-form") ? (
                            <>
                              <Button className="btn-info text-white"
                                onClick={() => handleView(getFormByType("infrared-form").url)}>
                                View
                              </Button>
                              <Button className="btn-danger text-white"
                                onClick={() => handleRevoke(getFormByType("infrared-form").id)}>
                                Revoke
                              </Button>
                            </>
                          ) : (
                            <Button onClick={handleInfrared}>
                              Sign Consent Form
                            </Button>
                          )}
                        </FlexBox>
                      </FlexBox>
                    </SimpleCard>

                    <SimpleCard className="membership-package-card mb-0 mb-md-3">
                      <FlexBox
                        direction="column"
                        alignItems="center"
                        justifyContent="between"
                        className="h-100 p-0 p-md-4"
                      >
                        <div className="w-100 text-center">
                          <h5 className="fw-bold text-primary">CryoChamber</h5>
                        </div>
                        <FlexBox gap="2">
                          {getFormByType("cryochamber-form") ? (
                            <>
                              <Button className="btn-info text-white"
                                onClick={() => handleView(getFormByType("cryochamber-form").url)}>
                                View
                              </Button>
                              <Button className="btn-danger text-white"
                                onClick={() => handleRevoke(getFormByType("cryochamber-form").id)}>
                                Revoke
                              </Button>
                            </>
                          ) : (
                            <Button onClick={handleCryoChamber}>
                              Sign Consent Form
                            </Button>
                          )}
                        </FlexBox>
                      </FlexBox>
                    </SimpleCard>

                    <SimpleCard className="membership-package-card mb-0 mb-md-3">
                      <FlexBox
                        direction="column"
                        alignItems="center"
                        justifyContent="between"
                        className="h-100 p-0 p-md-4"
                      >
                        <div className="w-100 text-center">
                          <h5 className="fw-bold text-primary">AirPod</h5>
                        </div>
                        <FlexBox gap="2">
                          {getFormByType("airpod-form") ? (
                            <>
                              <Button className="btn-info text-white"
                                onClick={() => handleView(getFormByType("airpod-form").url)}>
                                View
                              </Button>
                              <Button className="btn-danger text-white"
                                onClick={() => handleRevoke(getFormByType("airpod-form").id)}>
                                Revoke
                              </Button>
                            </>
                          ) : (
                            <Button onClick={handleAirPod}>
                              Sign Consent Form
                            </Button>
                          )}
                        </FlexBox>
                      </FlexBox>
                    </SimpleCard>
                  
                </FlexBox>
              </SimpleCard>
            </Col>

          </Row>
        )}
        {selectedTab === 2 && (
          <Row>
            <Col sm="4">
              <div className="d-flex flex-column gap-3">
                <SimpleCard
                  title="Select Clinic"
                  className="shadow-sm"
                  >
                  {/** Filter only clinics where enableSessionBooking is TRUE */}
                  {(() => {
                    const sessionBookingClinics = clinics.filter(
                      (c) => c.enableSessionBooking === true
                    );

                    return (
                      <SimpleDropdown
                        showAllTitle="All Clinics"
                        options={sessionBookingClinics.map((c) => c.name)}
                        values={sessionBookingClinics.map((c) => c.id)}
                        selectedValue={selectedClinicId}
                        onChange={handleClinicChange}
                        label="Select Clinic"
                      />
                    );
                  })()}
                </SimpleCard>
                {sessionTherapies.map((t) => (
                  <SimpleCard
                    key={t.id}
                    className="therapy-item p-3 mb-2 btn-black-gradient-session"
                  >
                    <FlexBox
                      direction="row"
                      alignItems="center"
                      justifyContent="between"
                      gap="2"
                    >
                      <div className="col-md-6">
                        <span className="fw-bold text-primary">{t.name}</span>
                        <div className="small text-muted">1 session</div>
                        <div className="small text-muted">{t.duration} minutes</div>
                        <FlexBox alignItems="center" gap="2">
                          {t.price != null && <span className="me-2">£{t.price}</span>}
                          {t.credits != null && (
                            <span className="badge bg-primary-subtle text-primary">
                              {t.credits} credit{t.credits === 1 ? "" : "s"}
                            </span>
                          )}
                        </FlexBox>
                      </div>
                      <Button className="" onClick={() => handleBookSession(t.id)}>Book Session</Button>
                    </FlexBox>
                  </SimpleCard>
                ))}
              </div>
              <style>{`
                .btn-black-gradient-session {
                  color: #fff !important;
                  border: 1px solid rgba(0, 0, 0, 0.175);
                  background: transparent;
                  font-weight: 600;
                  letter-spacing: 0.5px;
                  box-shadow: 0 2px 8px rgba(44,44,44,0.08);
                  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
                }
                .btn-black-gradient-session:hover {
                  color: #fff !important;
                  box-shadow: 0 4px 16px rgba(44,44,44,0.12);
                  transform: translateX(8px) scale(1.04);
                }
                .slide-btn {
                  min-width: 140px;
                  min-height: 48px;
                  font-size: 1.1rem;
                  border-radius: 12px;
                  padding: 0.75rem 2rem;
                  transition: transform 0.2s;
                }
              `}</style>
            </Col>
            <Col sm="8">
              <SimpleCard title="Buy Packages" className="shadow-sm"> 
                {/* <div className="mb-2 fw-bold text-primary">Your Credits: {totalCredits}</div> */}
                <FlexBox
                  direction="row"
                  gap="4"
                  justifyContent="left"
                  className="flex-wrap justify-content-start"
                >
                  {packages.map((p) => (
                    <SimpleCard key={p.id} className="package-card mb-4">
                      <FlexBox
                        direction="column"
                        alignItems="center"
                        justifyContent="between"
                        className="h-100 p-4"
                      >
                        <div className="w-100 text-center">
                          <h5 className="fw-bold text-primary">{p.credits} Credits</h5>
                          <h2 className="text-success fw-bold mb-2">£{p.price}</h2>
                          <p className="small text-muted mb-2">
                            {p.validity
                              ? `Valid until ${p.validity} months`
                              : "No expiry"}
                          </p>
                          <span className="badge bg-primary-subtle text-primary mb-3">
                            {p.name}
                          </span>
                        </div>
                        <Button className="" onClick={() => handleBuyPackage(p.id)}>
                          Buy Package
                        </Button>
                      </FlexBox>
                    </SimpleCard>
                  ))}
                </FlexBox>
                <style>{`
                  .package-card {
                    min-width: 229px;
                    max-width: 236px;
                    flex: 1 1 260px;
                    border-radius: 1.5rem;
                  }
                  .package-card:hover {
                    box-shadow: 0 0 10px 0 #b6c6f5;
                  }
                  .btn-black-gradient {
                    background: linear-gradient(90deg, #232526 0%, #414345 100%);
                    color: #fff !important;
                    border: none;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    box-shadow: 0 2px 8px rgba(44,44,44,0.08);
                    transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
                  }
                  .btn-black-gradient:hover {
                    background: linear-gradient(90deg, #414345 0%, #232526 100%);
                    color: #fff !important;
                    box-shadow: 0 4px 16px rgba(44,44,44,0.12);
                    transform: translateX(8px) scale(1.04);
                  }
                `}</style>
              </SimpleCard>
            </Col>
          </Row>
        )}
        {selectedTab === 3 && (
          <Row>
            <Col xs={12} lg={12}>
              <SimpleCard
                title="Buy Membership Packages"
                className="shadow-sm"
              >
                <FlexBox
                  direction="row"
                  gap="3"
                  justifyContent="left"
                  className="flex-wrap justify-content-center justify-content-lg-start"
                >
                  {membershipPackages.map((p) => (
                    <SimpleCard key={p.id} className="membership-package-card mb-0 mb-md-3">
                      <FlexBox
                        direction="column"
                        alignItems="center"
                        justifyContent="between"
                        className="h-100 p-0 p-md-4"
                      >
                        <div className="w-100 text-center">
                          <h5 className="fw-bold text-primary">{p.credits} Credits</h5>
                          <h2 className="text-success fw-bold mb-2">£{p.price}</h2>
                          <p className="small text-muted mb-2">
                            {p.discount}% discount
                          </p>
                          <h6 className="text-primary fw-bold mb-2">{p.name}</h6>
                          <span className="badge bg-primary-subtle text-primary mb-3">
                            £{p.pricePerCredit} per credit
                          </span>
                        </div>
                        <Button className="mb-2" onClick={() => handleBuyMembershipPackage(p.id)}>
                          Buy Membership
                        </Button>
                        <a
                          href="https://www.heal-wellness.co.uk/terms-and-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="badge text-primary"
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Terms &amp; Conditions
                        </a>
                      </FlexBox>
                    </SimpleCard>
                  ))}
                </FlexBox>
              </SimpleCard>
            </Col>

            <Col xs={12} lg={12} className="mt-4 mb-4">
              <SimpleCard
                title="Subscribe membership packages list"
                className="shadow-sm"
              >
                  <SimpleDataTable
                        data={buyMembershipPackagesList}
                        columns={PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS}
                        isLoading={loading}
                        defaultSortFieldId="date"
                        defaultSortAsc={true}
                        onRowClick={(row: BuyMembershipPackageList) => {
                        setSelectedMembershipPackageDetails(row);
                        setShowMembershipPackageDetails(true);
                      }}
                      />
              </SimpleCard>
            </Col>

            <SimpleModal
              open={showMembershipPackageDetails}
              close={() => setShowMembershipPackageDetails(false)}
              title="Membership Details"
              size="sm"
            >
              <PatientMembershipPackageSummaryTile
                {...selectedMembershipPackageDetails}
                onChangePatientMembershipPackageStatus={onChangePatientMembershipPackageStatus}
              />
            </SimpleModal>
          </Row>
        )}
        {selectedTab === 4 && (
            <SimpleDataTable
                data={sessionBookingsDetails}
                columns={PAITENT_SESSION_BOOKING_DETAILS_COLUMNS}
                isLoading={patientsLoading}
                defaultSortFieldId="date"
                defaultSortAsc={true}
                onRowClick={(row: PatientSessionBookingDetails) => {
                setSelectedSessionBookingDetails(row);
                setShowSessionBookingDetails(true);
                }}
            />
        )}
        {selectedTab === 5 && (
          <Row>
            <Col xs={12} lg={12}>
              <SimpleCard title="Notes" className="shadow-sm">
                <Formik
                  initialValues={{ notes: content ?? "" }}
                  enableReinitialize={true}
                  onSubmit={async (values, { setSubmitting }) => {
                    try {
                      setSubmitting(true);
                      console.log(API_ROUTES.PATIENT.updateNotes.replace(":patientId", patientId));
                      const notesUrl = decodeURIComponent(API_ROUTES.PATIENT.updateNotes).replace("{{PATIENT_ID}}", patientId);
                      await postData(
                        notesUrl,
                        {
                          notes: values.notes
                        },
                        () => {
                          toast.success("Notes updated successfully");
                        }
                      );

                    } catch (error) {
                      console.log("Notes submit error", error);
                      toast.error("Failed to update notes");
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ values, handleSubmit, setFieldValue, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                      <SimpleEditor
                        value={patientDetail?.notes || ""}
                        onChange={(val) => setFieldValue("notes", val)}
                        className="mh-250 auto-scroll-250"
                      />

                      <div className="text-center mt-3">
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? "Saving..." : "Save Notes"}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </SimpleCard>
            </Col>
          </Row>
        )}
        {selectedTab === 6 && (
        <SimpleCard title="Credits List" className="shadow-sm"
        action={
          <SimpleButton
            isLoading={loading}
            type="button"
            variant="primary"
            className="px-4"
            onClick={handleAddCredits}
          >
            Add Credits
          </SimpleButton>
        }
        >
        <SimpleDataTable
        data={patientCreditsList}
        columns={PATIENT_CREDITS_LIST_COLUMNS}
        defaultSortFieldId="createdAt"
        defaultSortAsc={false}
        />
        </SimpleCard>
        )}
        {selectedTab === 7 && (
        <SimpleCard title="Profile" className="shadow-sm">
          <Row className="justify-content-md-center">
            <Col sm="6">
              <Formik
                initialValues={{ 
                  firstName: patientDetail?.firstName || "", 
                  lastName: patientDetail?.lastName || "", 
                  email: patientDetail?.email || "", 
                  phone: patientDetail?.phone || "" 
                }}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    setSubmitting(true);
                    const profileUrl = decodeURIComponent(API_ROUTES.PATIENT.profile).replace("{{PATIENT_ID}}", patientId);
                    await postData(
                      profileUrl,
                      {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        phone: values.phone,
                      },
                      () => {
                        toast.success("Patient profile updated successfully");
                      }
                    );

                  } catch (error) {
                    toast.error("Failed to update patient profile");
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col xs={12} md={6}>
                        <SimpleTextbox
                          name="firstName"
                          label="First Name"
                          onChange={handleChange}
                          value={values.firstName}
                          required
                          validationSchema={NAME_VALIDATION_SCHEMA(
                            "Please enter first name"
                          )}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <SimpleTextbox
                          name="lastName"
                          label="Last Name"
                          onChange={handleChange}
                          value={values.lastName}
                          required
                          validationSchema={NAME_VALIDATION_SCHEMA(
                            "Please enter last name"
                          )}
                        />
                      </Col>    
                    </Row>
                    <Row>
                      <Col xs={12} md={12}>
                        <SimpleTextbox
                          name="email"
                          label="Patient Email"
                          onChange={handleChange}
                          value={values.email}
                          required
                          readOnly={true}
                          validationSchema={EMAIL_VALIDATION_SCHEMA}
                        />
                        <SimpleTextbox
                          name="phone"
                          label="Patient Phone"
                          className="mt-3"
                          onChange={handleChange}
                          value={values.phone}
                          required
                          validationSchema={PHONE_NUMBER_VALIDATION_SCHEMA}
                        />
                      </Col>
                    </Row>
                    <Form.Group className="form-group login-btn text-center">
                      <FlexBox
                        justifyContent="between"
                        className="w-100"
                        alignItems="center"
                      >
                        <Button
                          className={`px-4 w-md-auto btn ${patientDetail?.disabled ? "btn-success" : "btn-danger" }`}
                          type="button"
                          onClick={() => onEnableDisablePatient(patientId, patientDetail?.disabled ? false : true)}
                        >
                          {patientDetail?.disabled ? "Enable" : "Disable"}
                        </Button>
                        <Button
                          className="btn btn-primary px-4 w-md-auto"
                          type="submit"
                        >
                          Update Profile
                        </Button>
                      </FlexBox>
                    </Form.Group>
                  </Form>
                )}
              </Formik>
              <hr />
              <Button
                className="px-4 w-md-auto btn btn-danger"
                type="button"
                onClick={() => onDeletePatient(patientId)}
              >
                Delete Patient
              </Button>
              
            </Col>
          </Row>
        </SimpleCard>
        )}
    </SimpleCard>
    <SimpleModal
        open={showSessionBookingDetails}
        close={() => setShowSessionBookingDetails(false)}
        title="Session Details"
        size="sm"
        >
        <PatientSessionBookingSummaryTile
            fromTime={selectedSessionBookingDetails.fromTime}
            {...selectedSessionBookingDetails}
            onChangeSessionBookingStatus={onChangeSessionBookingStatus}
        />
    </SimpleModal>
    <SimpleModal
      open={showAddPatientCredits}
      close={() => setShowAddPatientCredits(false)}
      title="Add Credits"
      size="sm"
    >
      <Form onSubmit={handleCreditSubmit}>
        <Row>
          <Col xs={12} md={12}>
            <SimpleTextbox
              label="Credits"
              type="number"
              name="credits"
              value={creditValues.credits}
              onChange={handleCreditChange}
              required
              validationSchema={POSITIVE_NUMBER_VALIDATION_SCHEMA}
              min={1}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={12}>
            <SimpleTextbox
              label="Validity (in months)"
              type="number"
              name="validity"
              value={creditValues.validity}
              onChange={handleCreditChange}
              required
              validationSchema={POSITIVE_NUMBER_VALIDATION_SCHEMA}
              max={12}
              min={1}
            />
          </Col>
        </Row>
        <Row>
          <div className="text-center mt-3">
            <Button type="submit">Add Credits</Button>
          </div>
        </Row>
      </Form>
    </SimpleModal>
    </DashboardLayout>
  );
};

export default AdminPatientProfilePage;